import { Component, inject, input, signal, effect } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import {
    CatalogProductDetail,
    ProductTax,
    CatalogProductListItem,
    FamilyListItem,
    MagentoPayload,
    PricePoint,
    ProductChange
} from '../../core/models';

// Per-location catalog products tab — embedded in the merchant detail page and in the standalone
// /products page (no route of its own).
//
// The CATALOG half is a read-only projection (list, detail, change/price history) — nothing here writes
// to it. The one write is the "Is online" switch, which sets a PIN in a separate relational table: there
// is only ONE property, IsOnline, and the pin says who decides its value. No pin means XT's feed decides;
// a pin means an operator did, in either direction, and it survives every subsequent heartbeat.
//
// Effective IsOnline — what the switch shows and what the storefront gets — is `active && (pin ?? isOnline)`.
// XT's `active` (its soft delete) gates unconditionally, which is why the switch is disabled for an
// inactive product: a pin can overrule the feed's IsOnline but cannot resurrect a deleted item.
//
// locationId arrives from the parent as a signal input; the FIRST fetch is owned by the [lazy] p-table's
// onLazyLoad (fires on render), guarded so it no-ops until a locationId is present. The pins are fetched
// separately, keyed off the same locationId, because they page independently of the product list (a
// location has thousands of products and, typically, a handful of pins).
@Component({
    selector: 'app-products-tab',
    standalone: true,
    imports: [
        CardModule, ButtonModule, TableModule, TagModule, DialogModule, MessageModule,
        SelectModule, ToggleSwitchModule, InputTextModule, TooltipModule, FormsModule, DecimalPipe, DatePipe
    ],
    template: `
        <div class="flex flex-wrap items-center gap-2 mb-3">
            <input pInputText [(ngModel)]="search" (keyup.enter)="reload()" placeholder="Search SKU / name" class="w-72" />
            <p-select [options]="familyOptions()" [(ngModel)]="familyKey" (onChange)="reload()"
                      placeholder="All families" [showClear]="true" [filter]="true" styleClass="w-64" />
            <div class="flex items-center gap-2">
                <p-toggleswitch [(ngModel)]="activeOnly" (onChange)="reload()" inputId="activeOnly" />
                <label for="activeOnly" class="text-sm">Active only</label>
            </div>
            <p-button icon="pi pi-search" label="Search" size="small" (onClick)="reload()" />
            <p-button icon="pi pi-refresh" [text]="true" size="small" (onClick)="reload()" />
        </div>

        <!-- The count is the answer to "did my filter do anything?" — a page of 25 rows looks identical
             whether the catalog holds 30 products or 30,000, and the paginator's page numbers only imply
             it. Reported off total(), the server's count for the CURRENT filter, not items().length. -->
        <p-table [value]="items()" [loading]="loading()" [lazy]="true" [paginator]="true" [rows]="pageSize"
                 [totalRecords]="total()" (onLazyLoad)="onPage($event)" [first]="first()"
                 [showCurrentPageReport]="true"
                 currentPageReportTemplate="Showing {first} to {last} of {totalRecords} products">
            <ng-template pTemplate="header">
                <tr>
                    <th>SKU</th>
                    <th pSortableColumn="name">Name <p-sortIcon field="name" /></th>
                    <th>Type</th><th>Volume</th>
                    <th pSortableColumn="price">Price <p-sortIcon field="price" /></th>
                    <th>Active</th><th>Is online<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Normally follows XT. A manual change stays in place until you press auto." tooltipPosition="top"></i></th>
                    <th pSortableColumn="changes">Changes <p-sortIcon field="changes" /></th>
                    <th pSortableColumn="lastChanged">Last changed <p-sortIcon field="lastChanged" /></th>
                    <th></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-p>
                <tr class="cursor-pointer" (click)="open(p)">
                    <td class="font-medium whitespace-nowrap">{{ p.sku }}</td>
                    <td>{{ p.name }}
                        @if (p.familyKey) { <span class="text-surface-400 text-xs block">{{ p.familyKey }}</span> }
                    </td>
                    <td class="text-sm">{{ p.type }}@if (p.subType) { <span class="text-surface-400"> / {{ p.subType }}</span> }</td>
                    <td class="text-sm whitespace-nowrap">{{ p.volume || '—' }}</td>
                    <td class="tabular-nums whitespace-nowrap">{{ p.price | number: '1.2-2' }}
                        @if (p.onSalePrice > 0 && p.onSalePrice < p.price) {
                            <span class="text-primary text-xs block">sale {{ p.onSalePrice | number: '1.2-2' }}</span>
                        }
                    </td>
                    <td><p-tag [value]="p.active ? 'Active' : 'Inactive'" [severity]="p.active ? 'success' : 'secondary'" /></td>
                    <!-- ONE property. The switch is the EFFECTIVE IsOnline; flipping it pins the new value,
                         and "auto" releases the pin back to XT. stopPropagation because the row itself
                         opens the detail dialog. -->
                    <td (click)="$event.stopPropagation()">
                        <div class="flex items-center gap-2">
                            <p-toggleswitch [ngModel]="effectiveOnline(p)"
                                            [disabled]="isSaving(p.sku) || !p.active"
                                            (onChange)="setOnline(p.sku, $event.checked)" />
                            @if (pinOf(p.sku) !== null) {
                                <p-tag value="manual" severity="warn" />
                                <!-- The way back to feed control. Without it the pin is a one-way door. -->
                                <p-button label="auto" [text]="true" size="small" [disabled]="isSaving(p.sku)"
                                          (onClick)="setOnline(p.sku, null)" />
                                @if (pinOf(p.sku) !== p.isOnline) {
                                    <!-- Pinned against the feed: name what XT is saying, so the disagreement
                                         is visible rather than something the next sync appears to undo. -->
                                    <span class="text-xs text-surface-400">XT: {{ p.isOnline ? 'online' : 'offline' }}</span>
                                }
                            } @else if (!p.active) {
                                <span class="text-xs text-surface-400">inactive in XT</span>
                            }
                        </div>
                    </td>
                    <td class="tabular-nums text-center">{{ p.changeCount }}</td>
                    <td class="text-sm text-surface-500 whitespace-nowrap">{{ p.lastChangedAt | date: 'short' }}</td>
                    <td><p-button icon="pi pi-eye" [text]="true" size="small" (onClick)="open(p)" /></td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr><td colspan="10">{{ error() ?? 'No products in the catalog for this location.' }}</td></tr>
            </ng-template>
        </p-table>

        @if (overrideNotice(); as n) {
            <p-message [severity]="n.severity" styleClass="mt-3">{{ n.text }}</p-message>
        }

        <p-dialog [(visible)]="showDetail" [modal]="true" [style]="{ width: '56rem' }" [dismissableMask]="true"
                  [header]="'Product ' + (selectedSku() ?? '')">
            @if (detailLoading()) {
                <p class="text-sm text-surface-500">Loading…</p>
            } @else if (detailError()) {
                <p-message severity="error">{{ detailError() }}</p-message>
            } @else if (detail(); as d) {
                <div class="flex flex-wrap items-center gap-2 mb-4">
                    <span class="text-lg font-semibold">{{ d.state.name }}</span>
                    <p-tag [value]="d.state.active ? 'Active' : 'Inactive'" [severity]="d.state.active ? 'success' : 'secondary'" />
                    @if (d.discontinued) { <p-tag value="Discontinued" severity="danger" /> }
                    @if (d.state.isOhLiquorItem) { <p-tag value="OH Liquor" severity="warn" /> }
                    <!-- Same switch as the table's "Is online" column, same state, same handler — so the
                         dialog never disagrees with the row behind it. The standalone Online/Offline tag
                         that used to sit here is gone: it showed XT's raw value next to the effective one
                         and read as two competing answers to a single question. When they differ, the
                         "XT: …" hint below says so explicitly. -->
                    <span class="ml-auto flex items-center gap-2">
                        <span class="text-sm text-surface-500 dark:text-surface-400">Is online</span>
                        <p-toggleswitch [ngModel]="effectiveOnlineOf(d.state.sku, d.state.active, d.state.isOnline)"
                                        [disabled]="isSaving(d.state.sku) || !d.state.active"
                                        (onChange)="setOnline(d.state.sku, $event.checked)" />
                        @if (pinOf(d.state.sku) !== null) {
                            <p-tag value="manual" severity="warn" />
                            <p-button label="auto" [text]="true" size="small" [disabled]="isSaving(d.state.sku)"
                                      (onClick)="setOnline(d.state.sku, null)" />
                            @if (pinOf(d.state.sku) !== d.state.isOnline) {
                                <span class="text-xs text-surface-400">XT: {{ d.state.isOnline ? 'online' : 'offline' }}</span>
                            }
                        } @else if (!d.state.active) {
                            <span class="text-xs text-surface-400">inactive in XT</span>
                        }
                    </span>
                </div>

                <div class="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4 text-sm">
                    <div><b>SKU:</b> {{ d.state.sku }}</div>
                    <div><b>XT ID:</b> {{ d.state.xtId || '—' }}</div>
                    <div><b>External ID:</b> {{ d.state.externalId || '—' }}</div>
                    <div><b>Family:</b> {{ d.familyKey || '—' }}</div>
                    <div><b>Type:</b> {{ d.state.type || '—' }}</div>
                    <div><b>Sub-type:</b> {{ d.state.subType || '—' }}</div>
                    <div><b>Category:</b> {{ d.state.categoryName || '—' }} <span class="text-surface-400">({{ d.state.categoryId || '—' }})</span></div>
                    <div><b>Sub-category:</b> {{ d.state.subCategoryName || '—' }} <span class="text-surface-400">({{ d.state.subCategoryId || '—' }})</span></div>
                    <div><b>Volume:</b> {{ d.state.volume || '—' }}</div>
                    <div><b>Proof:</b> {{ d.state.proof || '—' }}</div>
                    <div><b>Charge unit:</b> {{ d.state.chargeUnit || '—' }}</div>
                    <div><b>Tax flags:</b> {{ d.state.taxFlags || '—' }}</div>
                    <div><b>Price:</b> <span class="tabular-nums">{{ d.state.price | number: '1.2-2' }}</span></div>
                    <div><b>On-sale:</b> <span class="tabular-nums">{{ d.state.onSalePrice | number: '1.2-2' }}</span></div>
                    <div><b>Cost:</b> <span class="tabular-nums">{{ d.state.cost | number: '1.2-2' }}</span></div>
                    <div><b>Mod price:</b> <span class="tabular-nums">{{ d.state.modPrice | number: '1.2-2' }}</span></div>
                    <div><b>Min qty:</b> {{ d.state.minQty }}</div>
                    <div><b>Limit qty:</b> {{ d.state.limitQty }}</div>
                    <div><b>Bottle limit:</b> {{ d.state.bottleLimit }}</div>
                    <div><b>Bottle icon:</b> {{ d.state.bottleIcon }}</div>
                    <div><b>Version:</b> {{ d.version }}</div>
                    <div><b>Changes:</b> {{ d.changeCount }}</div>
                    <div class="col-span-2 md:col-span-3 text-surface-500">
                        <b>First seen:</b> {{ d.firstSeenAt | date: 'medium' }} ·
                        <b>Last changed:</b> {{ d.lastChangedAt | date: 'medium' }} ·
                        <b>Last synced:</b> {{ d.lastSyncedAt | date: 'medium' }}
                    </div>
                    @if (d.state.shortDescription) {
                        <div class="col-span-2 md:col-span-3"><b>Description:</b> {{ d.state.shortDescription }}</div>
                    }
                </div>

                <h6>Tax at the register<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="What XT charges on one unit today: each percent tax of the slots the item is flagged for, on the current price, and the sin tax as its rate times the item's volume - never a percent. Magento's figure is on the order's line; a difference between the two is a rate or a ratio that differs between XT and Magento." tooltipPosition="top"></i></h6>
                @if (taxLoading()) {
                    <p class="text-sm text-surface-500">Loading…</p>
                } @else if (taxError()) {
                    <p-message severity="warn" styleClass="mb-3">{{ taxError() }}</p-message>
                } @else if (tax(); as t) {
                    @if (t.problem) {
                        <p class="text-sm text-surface-500 mb-3">{{ t.problem }}</p>
                    } @else if (!t.lines.length) {
                        <p class="text-sm text-surface-500 mb-3">XT flags this item for no tax slot: the register charges no tax on it.</p>
                    } @else {
                        <table class="w-full text-sm mb-3">
                            <thead>
                                <tr class="text-left text-surface-500">
                                    <th class="py-0.5">Slot</th><th class="py-0.5">Tax</th><th class="py-0.5">Rate</th><th class="py-0.5 text-right">On one unit at {{ t.price | number: '1.2-2' }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (l of t.lines; track l.group + l.name) {
                                    <tr>
                                        <td class="py-0.5">{{ l.group }} — {{ l.groupName }}</td>
                                        <td class="py-0.5">{{ l.name }}</td>
                                        <td class="py-0.5 tabular-nums">
                                            @if (l.kind === 'per-volume') {
                                                \${{ l.rate | number: '1.2-2' }} per {{ l.unit }} × {{ l.ratio | number: '1.0-4' }} {{ l.unit }}
                                            } @else {
                                                {{ l.rate | number: '1.0-4' }}%
                                            }
                                        </td>
                                        <td class="py-0.5 text-right tabular-nums">{{ l.amountPerUnit | number: '1.2-2' }}</td>
                                    </tr>
                                }
                                <tr class="font-medium border-t border-surface-200 dark:border-surface-700">
                                    <td class="py-1" colspan="3">Total tax per unit</td>
                                    <td class="py-1 text-right tabular-nums">{{ t.totalPerUnit | number: '1.2-2' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    }
                }

                <h6>Sent to Magento</h6>
                @if (payloadLoading()) {
                    <p class="text-sm text-surface-500">Loading…</p>
                } @else if (payloadError()) {
                    <p-message severity="warn" styleClass="mb-4">{{ payloadError() }}</p-message>
                } @else if (payload(); as m) {
                    @if (!m.sent) {
                        <p-message severity="info" styleClass="mb-4">
                            Nothing sent to Magento yet.
                        </p-message>
                    } @else {
                        <div class="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3 text-sm">
                            <div><b>Request status:</b> {{ m.requestStatus || '—' }}</div>
                            <div><b>Processed:</b> {{ m.processed ? 'yes' : 'no' }}</div>
                            <div><b>Magento ID:</b> {{ m.externalId || '—' }}</div>
                            <div><b>XT ID:</b> {{ m.xtId || '—' }}</div>
                            <div><b>Requests:</b> {{ m.requestsCount }}</div>
                            <div><b>Dispatches:</b> {{ m.dispatchedCount }}</div>
                            <div class="col-span-2 md:col-span-3 text-surface-500">
                                <b>Created:</b> {{ m.createdAt ? (m.createdAt | date: 'medium') : '—' }} ·
                                <b>Last modified:</b> {{ m.lastModified ? (m.lastModified | date: 'medium') : '—' }}
                            </div>
                            @if (m.error) {
                                <div class="col-span-2 md:col-span-3">
                                    <p-message severity="error">{{ m.error }}</p-message>
                                </div>
                            }
                        </div>

                        @if (m.decodeError) {
                            <p-message severity="warn" styleClass="mb-3">
                                The sent data could not be read — {{ m.decodeError }} The raw JSON is below.
                            </p-message>
                        }

                        @if (m.product; as mp) {
                            <div class="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3 text-sm">
                                <div><b>SKU:</b> {{ mp.sku || '—' }}</div>
                                <div><b>Name:</b> {{ mp.name || '—' }}</div>
                                <div><b>Price:</b> <span class="tabular-nums">{{ mp.price | number: '1.2-2' }}</span></div>
                                <!-- Status is the whole IsOnline chain's destination, so it is spelled out
                                     rather than left as a bare 1/2. -->
                                <div><b>Status:</b> {{ mp.statusLabel }} ({{ mp.status }})</div>
                                <div><b>Visibility:</b> {{ mp.visibilityLabel }} ({{ mp.visibility }})</div>
                                <div><b>Type:</b> {{ mp.typeId || '—' }}</div>
                                <div><b>Attribute set:</b> {{ mp.attributeSetId }}</div>
                                <div><b>Weight:</b> {{ mp.weight || '—' }}</div>
                                <div><b>Websites:</b> {{ mp.websiteIds.length ? mp.websiteIds.join(', ') : '—' }}</div>
                                <div class="col-span-2 md:col-span-3">
                                    <b>Categories:</b> {{ mp.categoryIds.length ? mp.categoryIds.join(', ') : '—' }}
                                </div>
                            </div>

                            <p class="text-xs text-surface-500 dark:text-surface-400 mb-2">
                                Custom attributes ({{ mp.customAttributes.length }})
                            </p>
                            <p-table [value]="mp.customAttributes" styleClass="p-datatable-sm mb-3">
                                <ng-template pTemplate="header">
                                    <tr><th>Attribute</th><th>Value</th></tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-a>
                                    <tr>
                                        <td class="text-sm font-medium whitespace-nowrap">{{ a.code }}</td>
                                        <td class="text-sm break-all">{{ a.value ?? '—' }}</td>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="emptymessage">
                                    <tr><td colspan="2" class="text-surface-500">No custom attributes.</td></tr>
                                </ng-template>
                            </p-table>
                        }

                        @if (m.rawPayload) {
                            <!-- Native <details>: a collapsible debug view costs no component and no state. -->
                            <details class="mb-4">
                                <summary class="text-sm text-surface-500 dark:text-surface-400 cursor-pointer">Raw JSON</summary>
                                <pre class="text-xs mt-2 p-2 overflow-x-auto bg-surface-100 dark:bg-surface-800 rounded">{{ m.rawPayload }}</pre>
                            </details>
                        }
                    }
                }

                <h6>Change history</h6>
                @if (historyLoading()) {
                    <p class="text-sm text-surface-500">Loading history…</p>
                } @else {
                    <p-table [value]="history()" styleClass="p-datatable-sm mb-4">
                        <ng-template pTemplate="header">
                            <tr><th>Type</th><th>Field</th><th>Old</th><th>New</th><th>When</th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-c>
                            <tr>
                                <td>
                                    <p-tag [value]="c.changeType" [severity]="changeSev(c.changeType)" />
                                    <!-- Only operator-driven rows carry an actor; the feed has none. -->
                                    @if (c.actor) {
                                        <span class="text-xs text-surface-500 dark:text-surface-400 block mt-1">{{ c.actor }}</span>
                                    }
                                </td>
                                <td class="text-sm">{{ c.field ?? '—' }}</td>
                                <td class="text-sm">{{ display(c.oldValue, c.oldDecimal) }}</td>
                                <td class="text-sm">{{ display(c.newValue, c.newDecimal) }}</td>
                                <td class="text-sm text-surface-500 whitespace-nowrap">{{ c.at | date: 'short' }}</td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="5" class="text-surface-500">No changes recorded.</td></tr>
                        </ng-template>
                    </p-table>
                }

                <h6>Price history</h6>
                @if (priceLoading()) {
                    <p class="text-sm text-surface-500">Loading price history…</p>
                } @else {
                    <p-table [value]="pricePoints()" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr><th>Field</th><th>Old</th><th>New</th><th>When</th></tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-pp>
                            <tr>
                                <td class="text-sm">{{ pp.field }}</td>
                                <td class="tabular-nums text-sm">{{ pp.oldValue == null ? '—' : (pp.oldValue | number: '1.2-2') }}</td>
                                <td class="tabular-nums text-sm">{{ pp.newValue == null ? '—' : (pp.newValue | number: '1.2-2') }}</td>
                                <td class="text-sm text-surface-500 whitespace-nowrap">{{ pp.at | date: 'short' }}</td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="4" class="text-surface-500">No price changes recorded.</td></tr>
                        </ng-template>
                    </p-table>
                }
            }
        </p-dialog>
    `
})
export class ProductsTab {
    private readonly api = inject(MiddlewareApi);

    // Signal input — the parent (merchant-detail tabs) passes [locationId].
    readonly locationId = input.required<string>();

    readonly items = signal<CatalogProductListItem[]>([]);
    readonly total = signal(0);
    readonly first = signal(0);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly pageSize = 25;

    search = '';
    familyKey: string | null = null;
    activeOnly = false;
    // Column sort, echoed to the server. null field = the catalog's default by-SKU order. Held as plain
    // fields (not signals) because only the loader reads them, and it re-runs on every table event.
    private sortField: string | null = null;
    private sortDesc = false;
    readonly familyOptions = signal<{ label: string; value: string }[]>([]);

    // Detail dialog state.
    readonly showDetail = signal(false);
    readonly selectedSku = signal<string | null>(null);
    readonly detail = signal<CatalogProductDetail | null>(null);
    readonly detailLoading = signal(false);
    readonly detailError = signal<string | null>(null);
    readonly history = signal<ProductChange[]>([]);
    readonly historyLoading = signal(false);
    readonly pricePoints = signal<PricePoint[]>([]);
    readonly priceLoading = signal(false);

    // --- Admin IsOnline pins --------------------------------------------------------------------
    // sku -> pinned IsOnline value. A Map, not a field on the row objects: the product list is re-fetched
    // on every page/sort/search and would drop any value merged into it, while the pins are fetched once
    // per location and survive all of that. A Map rather than a Set because the pin now carries a VALUE —
    // absent means "no pin, XT decides", which a Set could not distinguish from "pinned false".
    //
    // Held IMMUTABLE — every change assigns a NEW Map. The app runs zoneless
    // (provideZonelessChangeDetection), so mutating the existing one in place would not repaint.
    readonly pins = signal<ReadonlyMap<string, boolean>>(new Map<string, boolean>());
    readonly savingSkus = signal<ReadonlySet<string>>(new Set<string>());
    readonly overrideNotice = signal<{ severity: 'error' | 'warn' | 'success'; text: string } | null>(null);

    // "Sent to Magento" panel — its own fetch, because the request machine lives in the host's relational
    // store and the catalog detail endpoint deliberately cannot see it.
    readonly payload = signal<MagentoPayload | null>(null);
    readonly payloadLoading = signal(false);
    readonly payloadError = signal<string | null>(null);

    // "Tax at the register" panel - its own fetch too: the XT replica and the tax profile are the host's.
    readonly tax = signal<ProductTax | null>(null);
    readonly taxLoading = signal(false);
    readonly taxError = signal<string | null>(null);

    private familiesLoadedFor: string | null = null;
    private overridesLoadedFor: string | null = null;

    constructor() {
        // Load the family filter and the override set once a locationId is available (and again if it
        // changes). The product LIST is driven by the [lazy] table's onLazyLoad, so it is NOT triggered
        // here.
        effect(() => {
            const loc = this.locationId();
            if (loc && this.familiesLoadedFor !== loc) {
                this.familiesLoadedFor = loc;
                this.loadFamilies(loc);
            }
            if (loc && this.overridesLoadedFor !== loc) {
                this.overridesLoadedFor = loc;
                this.loadOverrides(loc);
            }
        });
    }

    private loadOverrides(loc: string): void {
        this.api.getProductOnlineOverrides(loc).subscribe({
            // The server returns SKUs already normalised the same way the catalog list does, so they
            // match p.sku without any normalising here.
            next: (r) => this.pins.set(new Map((r.overrides ?? []).map((o) => [o.sku, o.online]))),
            // A failed pin fetch must not blank the product table. Every switch then falls back to XT's
            // value, which is the honest default — we do not know of a pin — and the banner says why.
            error: (e) => {
                this.pins.set(new Map<string, boolean>());
                this.overrideNotice.set({
                    severity: 'warn',
                    text: `Could not load the manual IsOnline pins — ${this.describe(e)} `
                        + 'The switches below are showing what XT reports, which may not be what is set.'
                });
            }
        });
    }

    /** The pinned value for a SKU, or null when there is no pin and XT decides. */
    pinOf(sku: string): boolean | null {
        const pinned = this.pins().get(sku);
        return pinned === undefined ? null : pinned;
    }

    isSaving(sku: string): boolean {
        return this.savingSkus().has(sku);
    }

    /** What the storefront actually gets: `active && (pin ?? isOnline)`. The same rule the server folds
     *  into the Magento payload (ProductOnlineOverride.EffectiveOnline) — kept identical on purpose, so the
     *  switch shows the outcome rather than one of its inputs. */
    effectiveOnline(p: CatalogProductListItem): boolean {
        return this.effectiveOnlineOf(p.sku, p.active, p.isOnline);
    }

    /** The same rule addressed by parts, for the detail dialog (whose row model is the canonical state). */
    effectiveOnlineOf(sku: string, active: boolean, xtIsOnline: boolean): boolean {
        return active && (this.pinOf(sku) ?? xtIsOnline);
    }

    /**
     * Set one product's IsOnline by hand, or hand it back to XT.
     *
     * `online` is the value to pin, or null to release the pin. Flipping the switch always PINS: with no
     * pin the switch is showing XT's value, so moving it means "I want the other one and I want it to
     * stick" — which is the whole point, since the next heartbeat would otherwise undo it.
     *
     * Optimistic: the map is updated before the request goes out and restored from the exact previous
     * value if it fails, so a rejected change snaps back rather than lying until the next page load.
     *
     * A successful response with pushedToMagento=false is NOT a failure and must not revert — the pin row
     * is committed, only the immediate storefront push missed, and the next XT sync applies it. Reverting
     * there would show the operator the opposite of what is actually stored.
     */
    setOnline(sku: string, online: boolean | null): void {
        const loc = this.locationId();
        if (!loc) return;

        const previous = this.pins();

        this.pins.set(withPin(previous, sku, online));
        this.savingSkus.set(withMember(this.savingSkus(), sku, true));
        this.overrideNotice.set(null);

        this.api.setProductOnlineOverride(loc, sku, online).subscribe({
            next: (r) => {
                this.savingSkus.set(withMember(this.savingSkus(), sku, false));

                if (r?.pushedToMagento === false) {
                    this.overrideNotice.set({
                        severity: 'warn',
                        text: `${sku}: the change is saved, but Magento was not updated yet`
                            + `${r.magentoError ? ' — ' + r.magentoError : '.'} `
                            + 'The next product sync will apply it.'
                    });
                    return;
                }

                // The server's `effective` is authoritative — Active can make the outcome differ from what
                // was asked for, and saying "is now online" when the product is soft-deleted would be a lie.
                const effective = r?.effective;
                this.overrideNotice.set({
                    severity: 'success',
                    text: online === null
                        ? `${sku} follows XT again.`
                        : `${sku}: IsOnline pinned ${online ? 'on' : 'off'}`
                            + (effective != null && effective !== online
                                ? ` — still ${effective ? 'online' : 'offline'} on the storefront, because XT has it inactive.`
                                : '.')
                });

                // The Magento panel in the open dialog just went stale — this change dispatched a status
                // update. Refresh it rather than leave the old payload on screen contradicting the switch.
                if (this.selectedSku() === sku) this.loadPayload(loc, sku);
            },
            error: (e) => {
                this.pins.set(previous);
                this.savingSkus.set(withMember(this.savingSkus(), sku, false));
                this.overrideNotice.set({
                    severity: 'error',
                    text: e?.status === 403
                        ? 'Forbidden — changing IsOnline by hand needs the products:manage permission.'
                        : this.describe(e)
                });
            }
        });
    }

    private loadTax(loc: string, sku: string): void {
        this.taxLoading.set(true);
        this.taxError.set(null);
        this.api.getProductTax(loc, sku).subscribe({
            next: (t) => { this.tax.set(t); this.taxLoading.set(false); },
            error: (e) => {
                this.tax.set(null);
                this.taxError.set(`Could not compute the register's tax — ${this.describe(e)}`);
                this.taxLoading.set(false);
            }
        });
    }

    private loadPayload(loc: string, sku: string): void {
        this.payloadLoading.set(true);
        this.payloadError.set(null);
        this.api.getProductMagentoPayload(loc, sku).subscribe({
            next: (m) => { this.payload.set(m); this.payloadLoading.set(false); },
            // Its own error line, not the dialog's: the catalog half of the dialog is still perfectly
            // readable when only this panel fails.
            error: (e) => {
                this.payload.set(null);
                this.payloadError.set(`Could not load what was sent to Magento — ${this.describe(e)}`);
                this.payloadLoading.set(false);
            }
        });
    }

    private loadFamilies(loc: string): void {
        this.api.getLocationFamilies(loc, { pageSize: 200 }).subscribe({
            next: (r) => this.familyOptions.set(
                (r.items ?? []).map((f: FamilyListItem) => ({
                    label: `${f.displayName} (${f.variantCount})`,
                    value: f.familyKey
                }))
            ),
            error: () => this.familyOptions.set([])
        });
    }

    onPage(e: { first?: number | null; rows?: number | null; sortField?: string | string[] | null; sortOrder?: number | null }): void {
        // The [lazy] table reports paging AND sorting through this one event. sortOrder is 1 asc / -1 desc,
        // sortField the column's pSortableColumn key (or absent when unsorted). PrimeNG types sortField as a
        // string|string[] for its multi-sort mode; this grid is single-sort, so collapse to the first key.
        this.sortField = Array.isArray(e.sortField) ? (e.sortField[0] ?? null) : (e.sortField ?? null);
        this.sortDesc = e.sortOrder === -1;
        const first = e.first ?? 0;
        this.first.set(first);
        this.load(Math.floor(first / this.pageSize) + 1);
    }

    reload(): void {
        this.first.set(0);
        this.load(1);
    }

    private load(page: number): void {
        const loc = this.locationId();
        // Guard: the [lazy] table fires onLazyLoad on first render, which can precede the input being set.
        if (!loc) { this.loading.set(false); return; }
        this.loading.set(true);
        this.error.set(null);
        this.api.getLocationProducts(loc, {
            page,
            pageSize: this.pageSize,
            search: this.search || undefined,
            familyKey: this.familyKey || undefined,
            activeOnly: this.activeOnly || undefined,
            sort: this.sortField || undefined,
            desc: this.sortDesc || undefined
        }).subscribe({
            next: (r) => {
                this.items.set(r.items ?? []);
                this.total.set(r.total ?? 0);
                this.loading.set(false);
            },
            error: (e) => {
                this.error.set(this.describe(e));
                this.loading.set(false);
            }
        });
    }

    open(p: CatalogProductListItem): void {
        const loc = this.locationId();
        if (!loc) return;
        this.showDetail.set(true);
        this.selectedSku.set(p.sku);
        this.detail.set(null);
        this.detailError.set(null);
        this.detailLoading.set(true);
        this.history.set([]);
        this.pricePoints.set([]);
        this.payload.set(null);
        this.tax.set(null);

        this.loadPayload(loc, p.sku);
        this.loadTax(loc, p.sku);

        this.api.getLocationProduct(loc, p.sku).subscribe({
            next: (d) => { this.detail.set(d); this.detailLoading.set(false); },
            error: (e) => { this.detailError.set(this.describe(e)); this.detailLoading.set(false); }
        });

        this.historyLoading.set(true);
        this.api.getProductHistory(loc, p.sku, { pageSize: 100 }).subscribe({
            next: (r) => { this.history.set(r.items ?? []); this.historyLoading.set(false); },
            error: () => { this.history.set([]); this.historyLoading.set(false); }
        });

        this.priceLoading.set(true);
        this.api.getProductPriceHistory(loc, p.sku).subscribe({
            next: (r) => { this.pricePoints.set(r.points ?? []); this.priceLoading.set(false); },
            error: () => { this.pricePoints.set([]); this.priceLoading.set(false); }
        });
    }

    /** Prefer the typed decimal for price events; fall back to the string form. */
    display(value?: string | null, decimal?: number | null): string {
        if (decimal != null) return decimal.toFixed(2);
        return value != null && value !== '' ? value : '—';
    }

    changeSev(changeType: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        switch (changeType) {
            case 'Added': return 'success';
            case 'Price': return 'info';
            case 'Status': return 'warn';
            case 'Name': return 'secondary';
            case 'Category': return 'secondary';
            case 'Family': return 'secondary';
            // An operator acted, rather than the feed reporting. Worth standing out in the history: it is
            // the only change type with a person behind it.
            case 'AdminOverride': return 'danger';
            default: return 'secondary';
        }
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 403) return 'Forbidden — your role lacks the merchants:read permission.';
        const server = e?.error?.detail ?? e?.error?.message;
        return server ?? `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}

/** A copy of `set` with `value` added or removed. Copy, never mutate: the app is zoneless, so a signal
 *  holding a mutated-in-place Set is the same reference and does not repaint. */
function withMember(set: ReadonlySet<string>, value: string, present: boolean): ReadonlySet<string> {
    const next = new Set(set);
    if (present) next.add(value); else next.delete(value);
    return next;
}

/** A copy of `map` with `sku` pinned to `online`, or the pin removed when `online` is null. Same
 *  copy-never-mutate rule as above, for the same zoneless reason. */
function withPin(
    map: ReadonlyMap<string, boolean>, sku: string, online: boolean | null
): ReadonlyMap<string, boolean> {
    const next = new Map(map);
    if (online === null) next.delete(sku); else next.set(sku, online);
    return next;
}
